import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QrReader } from "react-qr-reader";
import ApiURl from "../Paths/Api";

export default function ScanPage() {
  const [qrData, setQrData] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [meetingData, setMeetingData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [iconPosition, setIconPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 80 });
  const navigate = useNavigate();
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isClick = useRef(true);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const facingMode = isMobile ? "environment" : "user";

  const fetchStatus = async () => {
    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        const error = new Error("Phone number not found in local storage");
        console.error("[fetchStatus] Error:", error.message);
        throw error;
      }

      console.log("[fetchStatus] Sending request to get_meetings.php", { phone });
      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        const error = new Error(`HTTP error: ${response.status} ${response.statusText}`);
        console.error("[fetchStatus] HTTP Error:", error.message, { status: response.status });
        throw error;
      }

      const data = await response.json();
      console.log("[fetchStatus] API Response:", data);

      if (data.success && data.data && data.data.length > 0) {
        const meeting = data.data[0];
        const meetingData = {
          purpose: meeting.purpose || "Unknown",
          location: meeting.location || "Unknown",
          time: meeting.time || new Date().toISOString(),
          status: meeting.status || "Unknown",
          description: meeting.description || "No description provided",
          attendees: Array.isArray(meeting.attendees) ? meeting.attendees : [],
        };
        console.log("[fetchStatus] Setting meetingData:", meetingData);
        setMeetingData(meetingData);
      } else {
        console.error("[fetchStatus] No meetings found:", data.message || "Empty data");
        setMeetingData(null);
      }
    } catch (err) {
      console.error("[fetchStatus] Error:", err.message, { stack: err.stack });
      setMeetingData(null);
    }
  };

  const handleVerify = async () => {
    if (!qrData) {
      console.error("[handleVerify] No QR data available");
      return;
    }
    setIsLoading(true);

    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        const error = new Error("Phone number not found in local storage");
        console.error("[handleVerify] Error:", error.message);
        alert("Phone number not found. Please log in again.");
        throw error;
      }

      console.log("[handleVerify] Sending verification request", { unique_id: qrData, phone });
      const response = await fetch(`${ApiURl}/verify.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unique_id: qrData, phone }),
      });

      if (!response.ok) {
        const error = new Error(`HTTP error: ${response.status} ${response.statusText}`);
        console.error("[handleVerify] HTTP Error:", error.message, { status: response.status });
        throw error;
      }

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
        console.log("[handleVerify] API Response:", data);
      } catch (err) {
        console.error("[handleVerify] JSON Parse Error:", err.message, { text });
        alert("Failed to parse server response.");
        return;
      }

      if (data.success) {
        console.log("[handleVerify] Verification successful");
        setIsVerified(true);
        setIsButtonDisabled(true);
        setEmailSent(true);
      } else {
        console.error("[handleVerify] Verification failed:", data.message);
        alert("Error during verification: " + data.message);
      }
    } catch (err) {
      console.error("[handleVerify] Error:", err.message, { stack: err.stack });
      alert("Error occurred during verification: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    console.log("[handleBack] Navigating back");
    navigate(-1);
  };

  const handleNewScan = () => {
    console.log("[handleNewScan] Resetting scan state");
    setQrData(null);
    setIsVerified(false);
    setIsButtonDisabled(false);
    setEmailSent(false);
    // Do not reset meetingData or showModal to preserve report
  };

  // Draggable Icon Logic
  const startDrag = (e) => {
    isDragging.current = true;
    isClick.current = true;
    dragOffset.current = {
      x: e.clientX - iconPosition.x,
      y: e.clientY - iconPosition.y,
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  const onDrag = (e) => {
    if (!isDragging.current) return;
    isClick.current = false;
    const newX = e.clientX - dragOffset.current.x;
    const newY = e.clientY - dragOffset.current.y;
    const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 64));
    const boundedY = Math.max(64, Math.min(newY, window.innerHeight - 64));
    setIconPosition({ x: boundedX, y: boundedY });
  };

  const stopDrag = () => {
    isDragging.current = false;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", stopDrag);
  };

  const handleIconClick = async () => {
    if (!isClick.current) return;
    console.log("[handleIconClick] Icon clicked", { showModal });
    await fetchStatus();
    setShowModal(true);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
      {/* Embedded CSS for Animations */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-grow {
          animation: grow 0.6s ease-out;
        }
        .animate-pulse-slow {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes grow {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 100;
        }
        .draggable-icon {
          position: fixed;
          z-index: 40;
          cursor: pointer;
        }
        .draggable-icon:hover {
          transform: scale(1.05);
          transition: transform 0.2s ease-in-out;
        }
        .modal-content {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 101;
        }
        .draggable-icon:active {
          cursor: grabbing;
        }
      `}</style>

      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-4 shadow-lg fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-xl font-bold tracking-tight">QR Code Scanner</div>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-base font-medium hover:bg-teal-700 transition-all duration-300 transform hover:scale-105 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="min-h-screen pt-20 p-6 lg:p-8 flex justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 animate-slide-in" style={{ marginTop: '70px' }}>
          <h2 className="text-2xl font-semibold text-teal-600 mb-6 text-center">Scan QR Code</h2>
          <p className="text-base text-gray-600 mb-6 text-center">Scan a QR code to verify event attendance</p>

          {/* Camera Toggle Button */}
          {!qrData && (
            <button
              onClick={() => setIsCameraOpen(!isCameraOpen)}
              className={`w-full mb-6 px-4 py-3 rounded-lg font-medium text-white transition-all flex items-center justify-center space-x-2 ${
                isCameraOpen ? 'bg-red-600 hover:bg-red-700' : 'bg-teal-500 hover:bg-teal-600'
              } animate-grow`}
            >
              {isCameraOpen ? (
                <>
                  <svg className="w-5 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Close Scanner</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Open Scanner</span>
                </>
              )}
            </button>
          )}

          {/* QR Scanner */}
          {isCameraOpen && !qrData && (
            <div className="relative w-full aspect-square border-2 border-gray-200 rounded-lg overflow-hidden mb-6 animate-grow">
              <QrReader
                constraints={{ facingMode }}
                onResult={(result, error) => {
                  if (error) console.error("[QrReader] Scan Error:", error.message);
                  if (result) {
                    console.log("[QrReader] Scanned QR Code:", result.text);
                    setQrData(result.text);
                    setIsCameraOpen(false);
                  }
                }}
                containerStyle={{ width: "100%", height: "100%" }}
                videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-3/4 h-3/4">
                  <div className="absolute inset-0 border-4 border-teal-400 rounded-lg" />
                  <div className="absolute top-0 left-0 right-0 h-2 bg-teal-500 animate-[pulse_2s_ease-in-out_infinite]"/>
                </div>
              </div>
            </div>
          )}

          {/* Scanned Result and Verification */}
          {qrData && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-slide-in">
              <h3 className="text-base font-medium text-gray-700 mb-2">Scanned QR Code:</h3>
              <div className="bg-white p-3 rounded text border-gray-200 border mb-4 overflow-x-auto">
                <p className="text-sm font-mono text-gray-800 break-all">{qrData}</p>
              </div>

              {isVerified ? (
                <div className="mb-4 p-3 bg-green-50 border border-gray-200 rounded-md animate-slide-in">
                  <p className="text-green-600 font-medium">✓ Already Verified</p>
                  {emailSent && (
                    <p className="text-green-500 text-sm mt-1">Confirmation email sent to visitor.</p>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleVerify}
                  disabled={isButtonDisabled || isLoading}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all duration-300 hover:bg-green-600 flex items-center justify-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed animate-grow-0"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-20 text-white" fill="none" viewBox="0 0 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Verify Attendance</span>
                    </>
                  )}
                </button>
              )}

              <button
                onClick={handleNewScan}
                className="w-full mt-3 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all animate-grow"
              >
                Scan Another Code
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Draggable Icon */}
      <div
        className="draggable-icon w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center shadow-lg"
        style={{ left: `${iconPosition.x}px`, top: `${iconPosition.y}px` }}
        onMouseDown={startDrag}
        onClick={handleIconClick}
      >
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      </div>

      {/* Meeting Data Modal */}
      {showModal && (
        <div className="modal-backdrop animate-fade-in" onClick={() => setShowModal(false)}>
          <div
            className="modal-content bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto animate-slide-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-teal-600">Meeting Report</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {meetingData ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Purpose</h4>
                  <p className="text-base text-gray-900">{meetingData.purpose}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Location</h4>
                  <p className="text-base text-gray-900">{meetingData.location}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Time</h4>
                  <p className="text-base text-gray-900">{new Date(meetingData.time).toLocaleString()}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Status</h4>
                  <p className="text-base text-gray-900 capitalize">{meetingData.status}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Description</h4>
                  <p className="text-base text-gray-900">{meetingData.description}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attendees</h4>
                  {meetingData.attendees.length === 0 ? (
                    <p className="text-sm text-gray-500">No attendees found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-teal-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Phone</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Role</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {meetingData.attendees.map((attendee) => (
                            <tr key={attendee.id} className="hover:bg-gray-50 transition-all">
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.name}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.email}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.phone}
                              </td>
                              <td className={`px-4 py-2 text-sm ${attendee.attended ? 'text-green-600' : 'text-red-600'}`}>
                                {attendee.role || "User"}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {attendee.attended ? (
                                  <span className="text-green-600">Attended</span>
                                ) : (
                                  <span className="text-red-600">Not Attended</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">There is no meeting data available</p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full py-6 text-center text-gray-500 text-sm bg-transparent mt-8">
        <a
          href="https://algo2botsinfotech.com/home/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-teal-600 transition-colors duration-300"
        >
          Powered by @ Algo2bots Infotech • {new Date().getFullYear()}
        </a>
      </footer>
    </div>
  );
}